// Color blindness simulation using Viénot (1999) / Brettel (1997) matrices
// + Machado (2009) anomalous trichromacy + environment simulations

export type ColorBlindType =
  | 'normal'
  | 'deuteranopia' | 'protanopia' | 'tritanopia' | 'achromatopsia'
  | 'deuteranomaly' | 'protanomaly' | 'tritanomaly';

export type EnvironmentType = 'none' | 'bright-sunlight' | 'low-light' | 'blurred';

export const COLOR_BLIND_LABELS: Record<ColorBlindType, string> = {
  normal: 'Normal Vision',
  deuteranopia: 'Deuteranopia (Red-Green, complete)',
  protanopia: 'Protanopia (Red-Green, complete)',
  tritanopia: 'Tritanopia (Blue-Yellow, complete)',
  achromatopsia: 'Achromatopsia (No Color)',
  deuteranomaly: 'Deuteranomaly (Red-Green, partial — 5% of males)',
  protanomaly: 'Protanomaly (Red-Green, partial — 1% of males)',
  tritanomaly: 'Tritanomaly (Blue-Yellow, partial — rare)',
};

export const COLOR_BLIND_SHORT: Record<ColorBlindType, string> = {
  normal: 'Normal',
  deuteranopia: 'Deuter.',
  protanopia: 'Protan.',
  tritanopia: 'Tritan.',
  achromatopsia: 'Achrom.',
  deuteranomaly: 'D-anomaly',
  protanomaly: 'P-anomaly',
  tritanomaly: 'T-anomaly',
};

export const ENVIRONMENT_LABELS: Record<EnvironmentType, string> = {
  none: 'None',
  'bright-sunlight': 'Bright Sunlight (washed out)',
  'low-light': 'Low Light / Night',
  blurred: 'Blurred Vision',
};

// Dichromatic matrices: transform linear RGB → simulated linear RGB (Viénot 1999)
// Anomalous trichromacy matrices: Machado (2009) at severity ~0.6 (moderate)
type MatrixKey = Exclude<ColorBlindType, 'normal' | 'achromatopsia'>;
const MATRICES: Record<MatrixKey, number[][]> = {
  deuteranopia: [
    [0.29031, 0.70969, 0.0],
    [0.29031, 0.70969, 0.0],
    [-0.02197, 0.02197, 1.0],
  ],
  protanopia: [
    [0.10889, 0.89111, 0.0],
    [0.10889, 0.89111, 0.0],
    [0.00447, -0.00447, 1.0],
  ],
  tritanopia: [
    [1.0, 0.1248, -0.1248],
    [0.0, 0.86354, 0.13646],
    [0.0, 0.86354, 0.13646],
  ],
  // Machado 2009, severity ≈ 0.6
  deuteranomaly: [
    [0.57418, 0.42582, 0.0],
    [0.17418, 0.82582, 0.0],
    [-0.01318, 0.01318, 1.0],
  ],
  protanomaly: [
    [0.46533, 0.53467, 0.0],
    [0.06533, 0.93467, 0.0],
    [0.00268, -0.00268, 1.0],
  ],
  tritanomaly: [
    [1.0, 0.06222, -0.06222],
    [0.0, 0.93030, 0.06970],
    [0.0, 0.29570, 0.70430],
  ],
};

const linearize = (c: number): number => {
  const v = c / 255;
  return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
};

const delinearize = (c: number): number => {
  const v = c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
  return Math.round(Math.min(255, Math.max(0, v * 255)));
};

export const simulateColorBlindness = (
  r: number,
  g: number,
  b: number,
  type: ColorBlindType,
): { r: number; g: number; b: number } => {
  if (type === 'normal') return { r, g, b };

  if (type === 'achromatopsia') {
    const gray = Math.round(0.2126 * r + 0.7152 * g + 0.0722 * b);
    return { r: gray, g: gray, b: gray };
  }

  const lr = linearize(r);
  const lg = linearize(g);
  const lb = linearize(b);

  const m = MATRICES[type];
  const nr = delinearize(m[0][0] * lr + m[0][1] * lg + m[0][2] * lb);
  const ng = delinearize(m[1][0] * lr + m[1][1] * lg + m[1][2] * lb);
  const nb = delinearize(m[2][0] * lr + m[2][1] * lg + m[2][2] * lb);

  return { r: nr, g: ng, b: nb };
};

/**
 * Simulate environment conditions applied to an RGB color.
 */
export const simulateEnvironment = (
  r: number,
  g: number,
  b: number,
  env: EnvironmentType,
): { r: number; g: number; b: number } => {
  if (env === 'none') return { r, g, b };

  const clamp = (v: number) => Math.round(Math.min(255, Math.max(0, v)));

  switch (env) {
    case 'bright-sunlight': {
      // Wash out: blend toward white, reduce contrast
      const washFactor = 0.4;
      return {
        r: clamp(r + (255 - r) * washFactor),
        g: clamp(g + (255 - g) * washFactor),
        b: clamp(b + (255 - b) * washFactor),
      };
    }
    case 'low-light': {
      // Scotopic shift: desaturate and darken, slight blue shift
      const gray = 0.2126 * r + 0.7152 * g + 0.0722 * b;
      const desatFactor = 0.6;
      const darkenFactor = 0.55;
      return {
        r: clamp((r * (1 - desatFactor) + gray * desatFactor) * darkenFactor),
        g: clamp((g * (1 - desatFactor) + gray * desatFactor) * darkenFactor),
        b: clamp((b * (1 - desatFactor) + gray * desatFactor) * darkenFactor * 1.1),
      };
    }
    case 'blurred': {
      // Blurred vision can't be per-pixel — return color unchanged.
      // The UI component will apply CSS blur filter instead.
      return { r, g, b };
    }
    default:
      return { r, g, b };
  }
};
